import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { SpinnerGap, ArrowsClockwise, Warning, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";

const COMPLEXITY_COLORS = {
  minimal: { bg: "bg-emerald-500/10", text: "text-emerald-400", bar: "bg-emerald-500" },
  low: { bg: "bg-blue-500/10", text: "text-blue-400", bar: "bg-blue-500" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-400", bar: "bg-amber-500" },
  high: { bg: "bg-red-500/10", text: "text-red-400", bar: "bg-red-500" },
};

function ScoreRing({ score }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444";
  const label = score >= 80 ? "Healthy" : score >= 60 ? "Needs Work" : "Critical";

  return (
    <div className="flex flex-col items-center gap-1" data-testid="health-score-ring">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="var(--border)" strokeWidth="6" />
        <circle
          cx="48" cy="48" r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 48 48)"
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
        <text x="48" y="44" textAnchor="middle" fill={color} fontSize="22" fontFamily="JetBrains Mono" fontWeight="700">{score}</text>
        <text x="48" y="60" textAnchor="middle" fill="var(--text-muted)" fontSize="9" fontFamily="Manrope">/100</text>
      </svg>
      <span className="text-[10px] font-medium" style={{ color }}>{label}</span>
    </div>
  );
}

function BarChart({ items, maxValue, labelKey = "name", valueKey = "lines", showComplexity = false }) {
  const max = maxValue || Math.max(...items.map(i => i[valueKey]), 1);
  return (
    <div className="space-y-1.5" data-testid="complexity-bar-chart">
      {items.map((item, idx) => {
        const pct = (item[valueKey] / max) * 100;
        const complexity = item.complexity || "minimal";
        const colors = COMPLEXITY_COLORS[complexity];
        return (
          <div key={item[labelKey] || idx} className="group">
            <div className="flex items-center gap-2 text-xs">
              <span className="w-[180px] truncate text-[var(--text-secondary)] font-mono text-[10px]" title={item.path || item[labelKey]}>
                {item[labelKey]}
              </span>
              <div className="flex-1 h-4 rounded-sm bg-[var(--bg-base)] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.max(pct, 1)}%` }}
                  transition={{ duration: 0.5, delay: idx * 0.03 }}
                  className={`h-full rounded-sm ${showComplexity ? colors.bar : "bg-[#FF4400]"}`}
                />
              </div>
              <span className="w-12 text-right font-mono text-[10px] text-[var(--text-muted)]">
                {item[valueKey].toLocaleString()}
              </span>
              {showComplexity && (
                <span className={`w-14 text-center text-[9px] px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                  {complexity}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendChart({ snapshots }) {
  if (!snapshots || snapshots.length < 2) {
    return <p className="text-xs text-[var(--text-muted)] text-center py-4">Need at least 2 snapshots for trends.</p>;
  }

  const maxScore = 100;
  const maxLoc = Math.max(...snapshots.map(s => s.total_loc), 1);
  const barW = Math.max(6, Math.min(24, Math.floor(300 / snapshots.length)));

  return (
    <div className="space-y-3" data-testid="health-trend-chart">
      <div className="flex items-end gap-1 h-24 px-1">
        {snapshots.map((s, i) => {
          const scorePct = (s.health_score / maxScore) * 100;
          const color = s.health_score >= 80 ? "#10b981" : s.health_score >= 60 ? "#f59e0b" : "#ef4444";
          return (
            <div key={s.date || i} className="flex-1 flex flex-col items-center gap-0.5"
              title={`${s.date}: Score ${s.health_score}, ${s.total_loc} LOC`}>
              <span className="text-[8px] font-mono text-[var(--text-muted)]">{s.health_score}</span>
              <div className="w-full flex justify-center">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.max(scorePct * 0.7, 2)}px` }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-sm"
                  style={{ width: `${barW}px`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[8px] text-[var(--text-muted)] font-mono px-1">
        <span>{snapshots[0]?.date?.slice(5)}</span>
        <span>{snapshots[snapshots.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

export function CodeHealthSection() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("overview");

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const [healthResp, historyResp] = await Promise.all([
        api.get("/admin/code-health"),
        api.get("/admin/code-health/history?days=30"),
      ]);
      setData(healthResp.data);
      setHistory(historyResp.data.snapshots || []);
    } catch (e) {
      console.error("Failed to load code health:", e);
      toast.error("Failed to load code health data");
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  // useMemo hooks must be called before any early returns (React rules of hooks)
  const sortedExtensions = useMemo(
    () => data?.extension_breakdown ? Object.entries(data.extension_breakdown).sort((a, b) => b[1].lines - a[1].lines) : [],
    [data?.extension_breakdown]
  );
  const sortedHighComplexity = useMemo(
    () => data?.high_complexity_files ? [...data.high_complexity_files].sort((a, b) => b.lines - a.lines) : [],
    [data?.high_complexity_files]
  );

  if (loading && !data) {
    return (
      <div className="flex justify-center py-12">
        <SpinnerGap size={24} className="animate-spin text-[#FF4400]" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, complexity_distribution, extension_breakdown, largest_files, high_complexity_files } = data;
  const prevSnapshot = history && history.length >= 2 ? history[history.length - 2] : null;

  const scoreDelta = prevSnapshot ? summary.health_score - prevSnapshot.health_score : 0;
  const locDelta = prevSnapshot ? summary.total_loc - prevSnapshot.total_loc : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="code-health-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-['JetBrains_Mono'] text-[var(--text-muted)]">CODE HEALTH</h3>
        <button
          onClick={loadHealth}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors"
          data-testid="refresh-health-btn"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* View Toggle */}
      <div className="flex gap-1 p-0.5 bg-[var(--bg-base)] rounded-md w-fit" data-testid="health-view-toggle">
        {[
          { id: "overview", label: "Overview" },
          { id: "files", label: "Files" },
          { id: "trends", label: "Trends" },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              view === v.id
                ? "bg-[#FF4400] text-white"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
            data-testid={`health-view-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "overview" && (
        <div className="space-y-5">
          {/* Score + KPIs row */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:row-span-2 flex items-center justify-center bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
              <ScoreRing score={summary.health_score} />
            </div>
            <KpiCard
              label="Total Files"
              value={summary.total_files}
              sub={`${summary.frontend_files} FE / ${summary.backend_files} BE`}
            />
            <KpiCard
              label="Total Lines"
              value={summary.total_loc.toLocaleString()}
              sub={`${summary.frontend_loc.toLocaleString()} FE / ${summary.backend_loc.toLocaleString()} BE`}
              delta={locDelta}
              deltaLabel="lines"
            />
            <KpiCard
              label="Avg. File Size"
              value={`${summary.avg_loc} LOC`}
              sub={`Threshold: ${data.thresholds.high}+ = complex`}
            />
            <KpiCard
              label="Complex Files"
              value={high_complexity_files.length}
              sub={`> ${data.thresholds.high} lines`}
              alert={high_complexity_files.length > 3}
            />
            <KpiCard
              label="Score Trend"
              value={scoreDelta > 0 ? `+${scoreDelta}` : scoreDelta === 0 ? "—" : `${scoreDelta}`}
              sub="vs previous snapshot"
              delta={scoreDelta}
            />
            <KpiCard
              label="Medium Files"
              value={complexity_distribution.medium}
              sub={`${data.thresholds.medium}–${data.thresholds.high - 1} lines`}
            />
          </div>

          {/* Complexity Distribution */}
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Complexity Distribution</h4>
            <div className="flex gap-2">
              {Object.entries(complexity_distribution).map(([level, count]) => {
                const colors = COMPLEXITY_COLORS[level];
                const total = summary.total_files || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={level} className="flex-1 text-center space-y-1">
                    <div className={`text-lg font-['JetBrains_Mono'] font-bold ${colors.text}`}>{count}</div>
                    <div className="h-2 rounded-full bg-[var(--bg-base)] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5 }}
                        className={`h-full rounded-full ${colors.bar}`}
                      />
                    </div>
                    <div className="text-[9px] text-[var(--text-muted)] capitalize">{level}</div>
                    <div className="text-[9px] font-mono text-[var(--text-muted)]">{pct}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* File Type Breakdown */}
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">By File Type</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {sortedExtensions.map(([ext, info]) => (
                <div key={ext} className="bg-[var(--bg-base)] rounded-md p-2.5 text-center">
                  <div className="text-xs font-mono font-bold text-[#FF4400]">{ext}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{info.count} files</div>
                  <div className="text-[10px] font-mono text-[var(--text-secondary)]">{info.lines.toLocaleString()} LOC</div>
                </div>
              ))}
            </div>
          </div>

          {/* High Complexity Alert */}
          {high_complexity_files.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 space-y-3" data-testid="high-complexity-alert">
              <div className="flex items-center gap-2">
                <Warning size={16} className="text-red-400" />
                <h4 className="text-xs font-medium text-red-400 uppercase tracking-wider">
                  {high_complexity_files.length} High-Complexity File{high_complexity_files.length !== 1 ? "s" : ""} ({data.thresholds.high}+ LOC)
                </h4>
              </div>
              <div className="space-y-1">
                {sortedHighComplexity.map(f => (
                  <div key={f.path} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-[var(--bg-base)]">
                    <span className="font-mono text-[10px] text-[var(--text-secondary)] truncate mr-2">{f.path}</span>
                    <span className="font-mono text-[10px] text-red-400 shrink-0">{f.lines.toLocaleString()} lines</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[var(--text-muted)]">
                Consider breaking these into smaller modules for better maintainability.
              </p>
            </div>
          )}
        </div>
      )}

      {view === "files" && (
        <div className="space-y-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Largest Files (Top 15)</h4>
            <BarChart items={largest_files} labelKey="name" valueKey="lines" showComplexity />
          </div>
        </div>
      )}

      {view === "trends" && (
        <div className="space-y-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Health Score Over Time</h4>
            <TrendChart snapshots={history} />
          </div>

          {history && history.length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-2">
              <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Snapshot History</h4>
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {[...history].reverse().map((s, i) => (
                  <div key={s.date || i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-[var(--bg-base)]">
                    <span className="font-mono text-[10px] text-[var(--text-secondary)]">{s.date}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] text-[var(--text-muted)]">{s.total_files} files</span>
                      <span className="font-mono text-[10px] text-[var(--text-muted)]">{s.total_loc?.toLocaleString()} LOC</span>
                      <span className={`font-mono text-[10px] font-bold ${
                        s.health_score >= 80 ? "text-emerald-400" : s.health_score >= 60 ? "text-amber-400" : "text-red-400"
                      }`}>
                        {s.health_score}/100
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function KpiCard({ label, value, sub, delta, deltaLabel, alert }) {
  return (
    <div className={`bg-[var(--bg-card)] border rounded-lg p-3 ${alert ? "border-red-500/30" : "border-[var(--border)]"}`}>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-1.5 mt-1">
        <span className={`text-lg font-['JetBrains_Mono'] font-bold ${alert ? "text-red-400" : "text-[var(--text-primary)]"}`}>{value}</span>
        {delta !== undefined && delta !== 0 && (
          <span className={`flex items-center gap-0.5 text-[10px] ${delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
            {delta > 0 ? <TrendUp size={10} /> : <TrendDown size={10} />}
            {deltaLabel && `${Math.abs(delta)} ${deltaLabel}`}
          </span>
        )}
        {delta === 0 && delta !== undefined && (
          <Minus size={10} className="text-[var(--text-muted)]" />
        )}
      </div>
      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</div>
    </div>
  );
}
